import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useAPIKeyContext } from "../../contexts/api-key-context";

interface ApiKeyModalProps {
  open: boolean;
  onClose: () => void;
}

export function ApiKeyModal({ open, onClose }: ApiKeyModalProps) {
  const { apiKey } = useAPIKeyContext();
  const [showApiKey, setShowApiKey] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = () => {
    if (apiKey) {
      window.navigator.clipboard.writeText(apiKey);
      enqueueSnackbar("API key copied to clipboard", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">API Key</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Use this API key to authenticate requests to the desplega.ai API.
          </Typography>
          <TextField
            label="Your API Key"
            variant="outlined"
            fullWidth
            type={showApiKey ? "text" : "password"}
            value={apiKey || ""}
            autoComplete="new-password"
            disabled
            size="small"
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: 12,
              },
            }}
            InputProps={{
              endAdornment: (
                <Stack direction="row" spacing={0} alignItems="center">
                  <Tooltip title={showApiKey ? "Hide API key" : "Show API key"} placement="top">
                    <IconButton
                      onClick={() => setShowApiKey(!showApiKey)}
                      edge="end"
                      size="small"
                      disabled={!apiKey}
                    >
                      {showApiKey ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy API key" placement="top">
                    <IconButton onClick={handleCopy} edge="end" size="small" disabled={!apiKey}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            }}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
